import * as React from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import FadeVirtuosoScroller from 'shared/newComponents/FadeVirtuosoScroller';
import {
  VIRTUAL_LIST_DEFAULT_ITEM_HEIGHT,
  VIRTUAL_LIST_VIRTUOSO_MIN_COUNT
} from 'shared/newEntities/components/VirtualizedList';

import './VirtualizedList.modules.scss';

type Props = {
  itemCount: number;
  itemContent: (index: number) => React.ReactNode;
  /** Virtuoso включается при itemCount > virtuosoMinCount */
  virtuosoMinCount?: number;
  /** Высота списка. Игнорируется, когда задан внешний скролл-контейнер. */
  maxHeight?: number | string;
  className?: string;
  defaultItemHeight?: number;
  /** Ключ, при смене значения которого список будет прокручен к началу */
  listResetKey?: string | number;
  increaseViewportBy?: number;
  /** Родительский элемент, в котором будет прокручиваться список */
  customScrollParent?: HTMLElement | null;
  customScrollParentRef?: React.RefObject<HTMLElement | null>;
};

const VirtualizedListScroller = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithRef<'div'>
>((props, ref) => <FadeVirtuosoScroller {...props} ref={ref} hideScrollbar />);

const VirtualizedList: React.FC<Props> = ({
  itemCount,
  itemContent,
  virtuosoMinCount = VIRTUAL_LIST_VIRTUOSO_MIN_COUNT,
  maxHeight = 200,
  className,
  defaultItemHeight = VIRTUAL_LIST_DEFAULT_ITEM_HEIGHT,
  listResetKey,
  increaseViewportBy = 200,
  customScrollParent = null,
  customScrollParentRef
}) => {
  const virtuosoRef = React.useRef<VirtuosoHandle>(null);
  const [resolvedScrollParent, setResolvedScrollParent] =
    React.useState<HTMLElement | null>(
      customScrollParent ?? customScrollParentRef?.current ?? null
    );

  React.useLayoutEffect(() => {
    const scrollParent =
      customScrollParent ?? customScrollParentRef?.current ?? null;

    setResolvedScrollParent((prev) =>
      prev === scrollParent ? prev : scrollParent
    );
  }, [customScrollParent, customScrollParentRef, itemCount]);

  React.useEffect(() => {
    if (listResetKey === undefined) {
      return;
    }

    virtuosoRef.current?.scrollToIndex({ index: 0 });
  }, [listResetKey]);

  const plainList = (
    <>
      {Array.from({ length: itemCount }, (_, index) => (
        <React.Fragment key={index}>{itemContent(index)}</React.Fragment>
      ))}
    </>
  );

  if (itemCount <= virtuosoMinCount) {
    return plainList;
  }

  const usesExternalScroll =
    Boolean(customScrollParent) || Boolean(customScrollParentRef);

  if (usesExternalScroll && !resolvedScrollParent) {
    return plainList;
  }

  if (resolvedScrollParent) {
    return (
      <Virtuoso
        ref={virtuosoRef}
        className={className}
        customScrollParent={resolvedScrollParent}
        totalCount={itemCount}
        defaultItemHeight={defaultItemHeight}
        increaseViewportBy={increaseViewportBy}
        itemContent={(index) => itemContent(index)}
      />
    );
  }

  const heightValue =
    typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;

  return (
    <Virtuoso
      ref={virtuosoRef}
      className={className}
      styleName="virtualized-list"
      style={{ height: heightValue, maxHeight: heightValue }}
      totalCount={itemCount}
      defaultItemHeight={defaultItemHeight}
      increaseViewportBy={increaseViewportBy}
      components={{ Scroller: VirtualizedListScroller }}
      itemContent={(index) => itemContent(index)}
    />
  );
};

export default React.memo(VirtualizedList);
